import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Spinner, Alert, Badge,
  Row,
  Col,
  InputGroup
} from 'react-bootstrap';
import { FaTrashAlt, FaInfoCircle, FaEdit, FaEye, FaEyeSlash, FaSearch } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { driversAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';
import '../../assets/css/Driver.css';

function Drivers() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = parseInt(searchParams.get('page')) || 1;
  const searchFromUrl = searchParams.get('search') || '';

  const [searchTerm, setSearchTerm] = useState(searchFromUrl);
  const location = useLocation();
  const highlightedUserId = location.state?.scrollToUser;

  const [pagination, setPagination] = useState({
    current_page: pageFromUrl,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  // Fetch drivers with pagination and search
  const fetchDrivers = async (page = 1, search = searchTerm) => {
    try {
      setLoading(true);
      setError('');
      const response = await driversAPI.approved({
        page,
        per_page: pagination.per_page,
        search,
      });

      if (response.success) {
        setData(response.data.data || []);
        setPagination(response.data.meta || {
          current_page: page,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 10,
          total: response.data.total || 0,
        });
      } else {
        setError(response.message || t('failed_to_fetch_drivers'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_drivers'));
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Synchronize with URL
  useEffect(() => {
    fetchDrivers(pageFromUrl, searchFromUrl);
  }, [pageFromUrl, searchFromUrl]);

  // Debounced Search Effect to update URL
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== searchFromUrl) {
        setSearchParams({ page: 1, search: searchTerm });
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    if (highlightedUserId) {
      const element = document.getElementById(`user-${highlightedUserId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [data]);

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await driversAPI.delete(deleteId);
      if (response.success) {
        setShowDelete(false);
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_delete_driver'));
      }
    } catch (err) {
      setError(t('failed_to_delete_driver'));
      console.error("Error deleting driver:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (id) => {
    try {
      const response = await driversAPI.toggleStatus(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_status'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_status'));
      console.error("Error toggling driver status:", err);
    }
  };

  const toggleEmailChecked = async (id) => {
    try {
      const response = await driversAPI.emailChecked(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_email'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_email'));
      console.error("Error toggling email:", err);
    }
  };

  const toggleSmsChecked = async (id) => {
    try {
      const response = await driversAPI.smsChecked(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_sms'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_sms'));
      console.error("Error toggling SMS:", err);
    }
  };

  const toggleBookingStatus = async (id) => {
    try {
      const response = await driversAPI.toggleBookingApproved(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_status'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_status'));
    }
  };

  const toggleFastBookingStatus = async (id) => {
    try {
      const response = await driversAPI.toggleFastBookingApproved(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_status'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_status'));
    }
  };

  const toggleTransportStatus = async (id) => {
    try {
      const response = await driversAPI.toggleTransportApproved(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_status'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_status'));
    }
  };

  const toggleSpecialBookingStatus = async (id) => {
    try {
      const response = await driversAPI.toggleSpecialBookingApproved(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_status'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_status'));
    }
  };

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  const clearSearch = () => {
    setSearchTerm('');
    setSearchParams({ page: 1, search: '' });
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h3 className="fw-bold mb-0">👤 {t('drivers_approved_list')}</h3>
        <Button 
          className="btn btn-color" 
          onClick={() => navigate('/admin/drivers')}
        >
          {t('drivers_list')}
        </Button>
      </div>

      {/* Search Bar */}
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={t('search_by_email')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button 
                variant="outline-secondary" 
                onClick={clearSearch}
                title={t("reset_search")}
              >
                ✕
              </Button>
            )}
          </InputGroup>
        </Col>
      </Row>


      {error && <Alert variant="danger">{error}</Alert>}

      <div className="table-container shadow-sm rounded">
        <div className='table-responsive'>
          <Table bordered hover className="custom-table mb-0 text-center">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>{t('first_name')}</th>
                <th>{t('last_name')}</th>
                <th>{t('email')}</th>
                <th>{t('phone')}</th>
                <th>{t('rating')}</th>
                <th>{t('status')}</th>
                <th>{t('mail')}</th>
                <th>{t('sms')}</th>
                <th>{t('approved')}</th>
                <th>{t('booking_approved')}</th>
                <th>{t('fast_booking_approved')}</th>
                <th>{t('transport_approved')}</th>
                <th>{t('special_booking_approved')}</th>
                <th>{t('action')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((driver, index) => (
                <tr key={driver.id} id={`user-${driver.id}`} className={driver.id === highlightedUserId ? "user-highlight" : ""}>
                  <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                  <td>{driver.firstname || '-'}</td>
                  <td>{driver.lastname || '-'}</td>
                  <td>
                    {searchTerm ? (
                      <span 
                        dangerouslySetInnerHTML={{
                          __html: driver.email?.replace(
                            new RegExp(searchTerm, 'gi'), 
                            match => `<mark class="bg-warning">${match}</mark>`
                          )
                        }} 
                      />
                    ) : (
                      driver.email
                    )}
                  </td>
                  <td>{driver.telphone || '-'}</td>
                  <td>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={`fs-4 ${star <= Math.round(driver.average_rating || 0) ? 'text-warning' : 'text-muted'}`}
                      >
                        ★
                      </span>
                    ))}
                  </td>
                  <td>
                    <Badge bg={driver.status === 'Approved' ? 'success' : 'danger'}>
                      {driver.status === 'Approved' ? t('active') : t('inactive')}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <Form.Check
                        type="switch"
                        id={`mail-${driver.id}`}
                        checked={driver.email_checked}
                        onChange={() => toggleEmailChecked(driver.id)}
                        className="custom-switch"
                      />
                      <span className="toggle-label">
                        {driver.email_checked ? t('on') : t('off')}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <Form.Check
                        type="switch"
                        id={`sms-${driver.id}`}
                        checked={driver.sms_checked}
                        onChange={() => toggleSmsChecked(driver.id)}
                        className="custom-switch"
                      />
                      <span className="toggle-label">
                        {driver.sms_checked ? t('on') : t('off')}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <Form.Check
                        type="switch"
                        id={`approved-${driver.id}`}
                        checked={driver.admin_approve === "2" || driver.admin_approve === 2}
                        onChange={() => toggleStatus(driver.id)}
                        className="custom-switch"
                      />
                      {/* <span className="toggle-label">
                        {driver.admin_approve === "2" || driver.admin_approve === 2 ? t('approved') : t('pending')}
                      </span> */}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <Form.Check
                        type="switch"
                        id={`booking-approved-${driver.id}`}
                        checked={!!driver.is_booking_approved}
                        onChange={() => toggleBookingStatus(driver.id)}
                        className="custom-switch"
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <Form.Check
                        type="switch"
                        id={`fast-booking-approved-${driver.id}`}
                        checked={!!driver.is_fast_booking_approved}
                        onChange={() => toggleFastBookingStatus(driver.id)}
                        className="custom-switch"
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <Form.Check
                        type="switch"
                        id={`transport-approved-${driver.id}`}
                        checked={!!driver.is_transport_approved}
                        onChange={() => toggleTransportStatus(driver.id)}
                        className="custom-switch"
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <Form.Check
                        type="switch"
                        id={`special-booking-approved-${driver.id}`}
                        checked={!!driver.is_special_booking_approved}
                        onChange={() => toggleSpecialBookingStatus(driver.id)}
                        className="custom-switch"
                      />
                    </div>
                  </td>
                  <td>
                    <Button
                      variant="outline-info"
                      className="me-2"
                      onClick={() => navigate(`/admin/drivers/details/${driver.id}`, { 
                        state: { 
                          fromPage: pagination.current_page, 
                          userId: driver.id,
                          search: searchTerm,
                          fromPath: location.pathname
                        } 
                      })}
                      title={t('view_details')}
                    >
                      <FaInfoCircle />
                    </Button>
                    <Button
                      variant="outline-danger"
                      onClick={() => {
                        setDeleteId(driver.id);
                        setShowDelete(true);
                      }}
                      title={t('delete')}
                    >
                      <FaTrashAlt />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      {pagination.total > 0 && (
        <PaginationComponent
          currentPage={pagination.current_page}
          lastPage={pagination.last_page}
          onPageChange={(newPage) => setSearchParams({ page: newPage, search: searchTerm })}
          disabled={loading}
        />
      )}

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('confirm_deletion')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('confirm_delete_driver')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            {t('cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? t('deleting') : t('delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Drivers;